import { Component, OnInit } from "@angular/core";

import { HttpClient } from "@angular/common/http";

import { ToastrService } from "ngx-toastr";

import { finalize } from "rxjs/operators";

import {
  Roles_Create,
  Roles_Update,
  Roles_Delete,
  Roles_ReadAllPaginated,
  SchoolMaster_ReadAllPaginated,
  Society_ReadAllPaginated,
} from "src/app/GlobalVariable";

import {
  HospitalMaster_ReadAllPaginated,
} from "src/app/GlobalVariable/Hospital.GlobalVariable";


interface RoleItem {
  roleId: number;

  roleName: string;
  roleCode: string;
  roleDesc: string;

  groupName: string;

  moduleName: string;
  moduleId: number;

  isActive: number;
  isDeleted: number;

  createdBy?: string;
  createdOn?: string;
}


interface RoleCreateRequest {
  roleName: string;

  roleCode: string;

  roleDesc: string;

  groupName: string;

  actionUser: string;

  moduleName: string;

  moduleId: number;
}


interface ModuleEntityItem {
  id: number;

  name: string;
}


@Component({
  selector: "app-roles-module",
  templateUrl: "./roles-module.component.html",
  styleUrls: ["./roles-module.component.css"],
})
export class RolesModuleComponent implements OnInit {

  // ============================================================
  // EXISTING ROLE DATA
  // ============================================================

  roles: RoleItem[] = [];

  searchText: string = "";

  loading: boolean = false;

  saving: boolean = false;

  showForm: boolean = false;

  pageNo: number = 1;

  pageSize: number = 10;


  // ============================================================
  // MODULE CONFIG
  // ============================================================

  moduleOptions = [
    "School",
    "Hospital",
    "Society",
    "Common",
  ];


  // ============================================================
  // NEW:
  // ONBOARDED ENTITY LIST FOR SELECTED MODULE
  // ============================================================

  moduleEntities: ModuleEntityItem[] = [];

  loadingModuleEntities: boolean = false;


  form = {
    roleId: 0,

    roleName: "",

    roleCode: "",

    roleDesc: "",

    /*
     * GroupName remains internally because existing backend
     * Roles_Create / Roles_Update expects it.
     *
     * We only remove the UI input.
     */
    groupName: "",

    moduleName: "",

    moduleId: 0,

    isActive: 1,
  };


  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
  ) {}


  ngOnInit(): void {
    this.loadRoles();
  }


  // ============================================================
  // READ ROLES
  // ============================================================

  loadRoles(): void {

    this.loading = true;


    const request = {

      pageSize: 1000,

      pageNo: 1,

      whereClause: null,

      orderByClause: "A.RoleName ASC",

    };


    this.http
      .post<any>(
        Roles_ReadAllPaginated,
        request,
      )
      .pipe(
        finalize(() => {

          this.loading = false;
        }),
      )
      .subscribe({

        next: (response) => {

          console.log(
            "Roles ReadAllPaginated Response:",
            response,
          );


          this.roles =
            this.extractArray(response)
              .map(
                (x: any) =>
                  this.normalizeRole(x),
              )
              .filter(
                (x) =>
                  x.isDeleted === 0,
              );


          this.ensurePage();
        },


        error: (error) => {

          console.error(
            "Roles Read Error:",
            error,
          );


          this.toastr.error(
            "Unable to load roles.",
          );
        },

      });
  }


  // ============================================================
  // NEW:
  // MODULE CHANGE
  // ============================================================

  onModuleNameChange(
    preserveCurrentModuleId: boolean = false,
  ): void {

    const moduleName =
      String(
        this.form.moduleName || "",
      ).trim();


    /*
     * Group Name input is removed from UI.
     *
     * Existing backend still expects GroupName,
     * therefore for new/module-changed roles we automatically
     * keep GroupName same as ModuleName.
     */
    if (!preserveCurrentModuleId) {

      this.form.groupName =
        moduleName;

      this.form.moduleId = 0;
    }


    const selectedId =
      preserveCurrentModuleId
        ? Number(
            this.form.moduleId || 0,
          )
        : 0;


    this.moduleEntities = [];


    if (!moduleName) {

      this.form.moduleId = 0;

      return;
    }


    /*
     * Common is not tied to one onboarded institution.
     */
    if (
      this.normalizeModuleName(
        moduleName,
      ) === "common"
    ) {

      this.form.moduleId = 0;

      return;
    }


    this.loadModuleEntities(
      moduleName,
      selectedId,
    );
  }


  // ============================================================
  // LOAD ONBOARDED ENTITY
  // School -> completed schools
  // Hospital -> completed hospitals
  // Society -> completed societies
  // ============================================================

  private loadModuleEntities(
    moduleName: string,
    selectedId: number = 0,
  ): void {

    const normalizedModule =
      this.normalizeModuleName(
        moduleName,
      );


    let requestUrl = "";


    if (
      normalizedModule === "school"
    ) {

      requestUrl =
        SchoolMaster_ReadAllPaginated;

    } else if (
      normalizedModule === "hospital"
    ) {

      requestUrl =
        HospitalMaster_ReadAllPaginated;

    } else if (
      normalizedModule === "society"
    ) {

      requestUrl =
        Society_ReadAllPaginated;

    } else {

      this.moduleEntities = [];

      this.form.moduleId = 0;

      return;
    }


    const request = {

      pageSize: 1000,

      pageNo: 1,

      whereClause: null,

      orderByClause: null,

    };


    this.loadingModuleEntities = true;


    console.log(
      `${moduleName} onboarded entity request:`,
      request,
    );


    this.http
      .post<any>(
        requestUrl,
        request,
      )
      .pipe(
        finalize(() => {

          this.loadingModuleEntities =
            false;
        }),
      )
      .subscribe({

        next: (response) => {

          console.log(
            `${moduleName} onboarded entity response:`,
            response,
          );


          const records =
            this.extractArray(
              response,
            );


          this.moduleEntities =
            records
              .filter(
                (item: any) =>
                  this.isValidOnboardedEntity(
                    item,
                  ),
              )
              .map(
                (item: any) =>
                  this.mapModuleEntity(
                    item,
                    normalizedModule,
                  ),
              )
              .filter(
                (item) =>
                  item.id > 0 &&
                  !!item.name,
              )
              .sort(
                (a, b) =>
                  a.name.localeCompare(
                    b.name,
                  ),
              );


          /*
           * EDIT FLOW:
           * Preserve currently stored ModuleId if it exists
           * in the onboarded entity list.
           */
          if (
            selectedId > 0 &&
            this.moduleEntities.some(
              (item) =>
                Number(item.id) ===
                Number(selectedId),
            )
          ) {

            this.form.moduleId =
              selectedId;

          } else if (
            selectedId <= 0
          ) {

            this.form.moduleId = 0;
          }

        },


        error: (error) => {

          console.error(
            `${moduleName} onboarded entity error:`,
            error,
          );


          this.moduleEntities = [];


          this.toastr.error(
            `Unable to load onboarded ${this.getModuleEntityPluralLabel(moduleName)}.`,
          );
        },

      });
  }


  // ============================================================
  // ONBOARDING STATUS FILTER
  // ============================================================

  private isValidOnboardedEntity(
    item: any,
  ): boolean {

    const isActive =
      Number(
        item?.isActive ??
        item?.IsActive ??
        1,
      );


    const isDeleted =
      Number(
        item?.isDeleted ??
        item?.IsDeleted ??
        0,
      );


    if (
      isActive !== 1 ||
      isDeleted !== 0
    ) {

      return false;
    }


    const onboardingRaw =
      item?.onBoardingStatus ??
      item?.OnBoardingStatus ??
      item?.onboardingStatus ??
      item?.OnboardingStatus;


    /*
     * School/Hospital ReadAllPaginated already returns
     * completed rows.
     *
     * Some APIs may not return OnBoardingStatus in response.
     * In that case keep the active result.
     */
    if (
      onboardingRaw === undefined ||
      onboardingRaw === null ||
      String(onboardingRaw).trim() === ""
    ) {

      return true;
    }


    const status =
      String(onboardingRaw)
        .trim()
        .toLowerCase();


    return (
      status === "completed" ||
      status === "complete" ||
      status === "onboarded" ||
      status === "onboarding completed"
    );
  }


  // ============================================================
  // MAP SCHOOL / HOSPITAL / SOCIETY TO COMMON DROPDOWN MODEL
  // ============================================================

  private mapModuleEntity(
    item: any,
    normalizedModule: string,
  ): ModuleEntityItem {

    if (
      normalizedModule === "school"
    ) {

      return {

        id:
          Number(
            item?.schoolId ??
            item?.SchoolId ??
            0,
          ),

        name:
          String(
            item?.schoolName ??
            item?.SchoolName ??
            "",
          ).trim(),

      };
    }


    if (
      normalizedModule === "hospital"
    ) {

      return {

        id:
          Number(
            item?.hospitalId ??
            item?.HospitalId ??
            0,
          ),

        name:
          String(
            item?.hospitalName ??
            item?.HospitalName ??
            "",
          ).trim(),

      };
    }


    if (
      normalizedModule === "society"
    ) {

      return {

        id:
          Number(
            item?.societyId ??
            item?.SocietyId ??
            0,
          ),

        name:
          String(
            item?.societyName ??
            item?.SocietyName ??
            "",
          ).trim(),

      };
    }


    return {
      id: 0,
      name: "",
    };
  }


  // ============================================================
  // MODULE ENTITY UI HELPERS
  // ============================================================

  get requiresModuleEntity(): boolean {

    const moduleName =
      this.normalizeModuleName(
        this.form.moduleName,
      );


    return (
      moduleName === "school" ||
      moduleName === "hospital" ||
      moduleName === "society"
    );
  }


  get moduleEntityLabel(): string {

    const moduleName =
      this.normalizeModuleName(
        this.form.moduleName,
      );


    if (
      moduleName === "school"
    ) {

      return "Onboarded School";
    }


    if (
      moduleName === "hospital"
    ) {

      return "Onboarded Hospital";
    }


    if (
      moduleName === "society"
    ) {

      return "Onboarded Society";
    }


    return "Onboarded Entity";
  }


  get moduleEntityPlaceholder(): string {

    if (
      !this.form.moduleName
    ) {

      return "Select Module first";
    }


    if (
      !this.requiresModuleEntity
    ) {

      return "Not required for Common";
    }


    return `Select ${this.moduleEntityLabel}`;
  }


  private getModuleEntityPluralLabel(
    moduleName: string,
  ): string {

    const normalized =
      this.normalizeModuleName(
        moduleName,
      );


    if (
      normalized === "school"
    ) {

      return "schools";
    }


    if (
      normalized === "hospital"
    ) {

      return "hospitals";
    }


    if (
      normalized === "society"
    ) {

      return "societies";
    }


    return "entities";
  }


  // ============================================================
  // SEARCH
  // ============================================================

  get filteredRoles(): RoleItem[] {

    const search =
      this.searchText
        .trim()
        .toLowerCase();


    if (!search) {

      return this.roles;
    }


    return this.roles.filter(
      (role) =>

        String(
          role.roleName || "",
        )
          .toLowerCase()
          .includes(search)

        ||

        String(
          role.roleCode || "",
        )
          .toLowerCase()
          .includes(search)

        ||

        String(
          role.groupName || "",
        )
          .toLowerCase()
          .includes(search)

        ||

        String(
          role.moduleName || "",
        )
          .toLowerCase()
          .includes(search),
    );
  }


  // ============================================================
  // PAGINATION
  // ============================================================

  get paginatedRoles(): RoleItem[] {

    const start =
      (
        this.pageNo - 1
      ) *
      this.pageSize;


    return this.filteredRoles.slice(
      start,
      start +
      this.pageSize,
    );
  }


  get totalPages(): number {

    return Math.max(
      1,
      Math.ceil(
        this.filteredRoles.length /
        this.pageSize,
      ),
    );
  }


  // ============================================================
  // OPEN CREATE
  // ============================================================

  openCreate(): void {

    this.resetForm();

    this.showForm = true;
  }


  // ============================================================
  // EDIT
  // ============================================================

  editRole(
    role: RoleItem,
  ): void {

    this.form = {

      roleId:
        Number(
          role.roleId || 0,
        ),

      roleName:
        String(
          role.roleName || "",
        ),

      roleCode:
        String(
          role.roleCode || "",
        ),

      roleDesc:
        String(
          role.roleDesc || "",
        ),

      /*
       * Preserve existing GroupName during edit.
       * It is hidden from UI only.
       */
      groupName:
        String(
          role.groupName ||
          role.moduleName ||
          "",
        ),

      moduleName:
        String(
          role.moduleName || "",
        ),

      moduleId:
        Number(
          role.moduleId || 0,
        ),

      isActive:
        Number(
          role.isActive ?? 1,
        ),

    };


    /*
     * Load entity dropdown and restore stored ModuleId.
     */
    this.onModuleNameChange(
      true,
    );


    this.showForm = true;


    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }


  // ============================================================
  // CANCEL
  // ============================================================

  cancelForm(): void {

    this.showForm = false;

    this.resetForm();
  }


  // ============================================================
  // ROLE NAME BLUR
  // ============================================================

  onRoleNameBlur(): void {

    if (
      this.form.roleCode.trim()
    ) {

      return;
    }


    this.form.roleCode =
      this.slugify(
        this.form.roleName,
      );
  }


  // ============================================================
  // SAVE ROLE
  // ============================================================

  saveRole(): void {

    if (
      this.saving
    ) {

      return;
    }


    const roleName =
      String(
        this.form.roleName || "",
      ).trim();


    const roleCode =
      String(
        this.form.roleCode || "",
      ).trim();


    const roleDesc =
      String(
        this.form.roleDesc || "",
      ).trim();


    const moduleName =
      String(
        this.form.moduleName || "",
      ).trim();


    const moduleId =
      Number(
        this.form.moduleId || 0,
      );


    /*
     * GROUP NAME INPUT REMOVED.
     *
     * Existing role:
     * preserve stored GroupName.
     *
     * New role/module change:
     * use ModuleName.
     */
    const groupName =
      String(
        this.form.groupName ||
        moduleName ||
        "",
      ).trim();


    // ==========================================================
    // VALIDATION
    // ==========================================================

    if (!roleName) {

      this.toastr.warning(
        "Role Name is required.",
      );

      return;
    }


    if (!roleCode) {

      this.toastr.warning(
        "Role Code is required.",
      );

      return;
    }


    if (!moduleName) {

      this.toastr.warning(
        "Module Name is required.",
      );

      return;
    }


    /*
     * School / Hospital / Society must select
     * an onboarded entity.
     *
     * Common keeps ModuleId = 0.
     */
    if (
      this.requiresModuleEntity &&
      moduleId <= 0
    ) {

      this.toastr.warning(
        `Please select ${this.moduleEntityLabel}.`,
      );

      return;
    }


    if (
      Number.isNaN(moduleId) ||
      moduleId < 0
    ) {

      this.toastr.warning(
        "Please select a valid onboarded entity.",
      );

      return;
    }


    // ==========================================================
    // UPDATE EXISTING ROLE
    // ==========================================================

    if (
      Number(
        this.form.roleId,
      ) > 0
    ) {

      this.updateRole();

      return;
    }


    // ==========================================================
    // DUPLICATE ROLE CODE CHECK
    // ==========================================================

    const duplicate =
      this.roles.some(
        (role) =>

          role.isDeleted === 0

          &&

          String(
            role.roleCode || "",
          )
            .trim()
            .toLowerCase() ===
          roleCode.toLowerCase(),
      );


    if (
      duplicate
    ) {

      this.toastr.warning(
        "Role Code already exists.",
      );

      return;
    }


    // ==========================================================
    // CREATE
    // ==========================================================

    this.createRole({

      roleName,

      roleCode,

      roleDesc,

      groupName,

      moduleName,

      moduleId,

    });
  }


  // ============================================================
  // CREATE ROLE
  // ============================================================

  private createRole(
    values: {
      roleName: string;
      roleCode: string;
      roleDesc: string;
      groupName: string;
      moduleName: string;
      moduleId: number;
    },
  ): void {

    const actionUser =
      this.getActionUser();


    const request:
      RoleCreateRequest = {

      roleName:
        values.roleName.trim(),

      roleCode:
        values.roleCode.trim(),

      roleDesc:
        values.roleDesc.trim(),

      /*
       * Backend contract remains unchanged.
       */
      groupName:
        values.groupName.trim(),

      actionUser:
        actionUser,

      moduleName:
        values.moduleName.trim(),

      moduleId:
        Number(
          values.moduleId,
        ),

    };


    console.log(
      "========================================",
    );

    console.log(
      "Roles Create URL:",
      Roles_Create,
    );

    console.log(
      "Roles Create Request:",
      request,
    );

    console.log(
      "Roles Create Request JSON:",
      JSON.stringify(
        request,
      ),
    );

    console.log(
      "========================================",
    );


    this.saving = true;


    this.http
      .post<any>(
        Roles_Create,
        request,
      )
      .pipe(
        finalize(() => {

          this.saving = false;
        }),
      )
      .subscribe({

        next: (response) => {

          console.log(
            "Roles Create Success:",
            response,
          );


          this.toastr.success(
            "Role created successfully.",
          );


          this.showForm = false;

          this.resetForm();

          this.pageNo = 1;

          this.loadRoles();
        },


        error: (error) => {

          console.error(
            "========================================",
          );

          console.error(
            "Roles Create Error:",
            error,
          );

          console.error(
            "Roles Create Status:",
            error?.status,
          );

          console.error(
            "Roles Create Status Text:",
            error?.statusText,
          );

          console.error(
            "Roles Create Error Body:",
            error?.error,
          );

          console.error(
            "Roles Create Request That Failed:",
            request,
          );

          console.error(
            "========================================",
          );


          this.showError(
            error,
            "Unable to create role.",
          );
        },

      });
  }


  // ============================================================
  // UPDATE ROLE
  // ============================================================

  private updateRole(): void {

    const moduleName =
      String(
        this.form.moduleName || "",
      ).trim();


    const request = {

      roleId:
        Number(
          this.form.roleId,
        ),

      roleName:
        String(
          this.form.roleName || "",
        ).trim(),

      roleCode:
        String(
          this.form.roleCode || "",
        ).trim(),

      roleDesc:
        String(
          this.form.roleDesc || "",
        ).trim(),

      /*
       * Preserve backend field.
       */
      groupName:
        String(
          this.form.groupName ||
          moduleName ||
          "",
        ).trim(),

      moduleName:
        moduleName,

      moduleId:
        Number(
          this.form.moduleId || 0,
        ),

      isActive:
        Number(
          this.form.isActive,
        ),

      actionUser:
        this.getActionUser(),

    };


    console.log(
      "Roles Update Request:",
      request,
    );


    this.saving = true;


    this.http
      .post<any>(
        Roles_Update,
        request,
      )
      .pipe(
        finalize(() => {

          this.saving = false;
        }),
      )
      .subscribe({

        next: (response) => {

          console.log(
            "Roles Update Success:",
            response,
          );


          this.toastr.success(
            "Role updated successfully.",
          );


          this.showForm = false;

          this.resetForm();

          this.loadRoles();
        },


        error: (error) => {

          console.error(
            "Role Update Error:",
            error,
          );


          this.showError(
            error,
            "Unable to update role.",
          );
        },

      });
  }


  // ============================================================
  // DELETE ROLE
  // ============================================================

  deleteRole(
    role: RoleItem,
  ): void {

    if (
      !window.confirm(
        `Delete role "${role.roleName}"?`,
      )
    ) {

      return;
    }


    const request = {

      roleId:
        Number(
          role.roleId,
        ),

      actionUser:
        this.getActionUser(),

    };


    this.http
      .post<any>(
        Roles_Delete,
        request,
      )
      .subscribe({

        next: () => {

          this.toastr.success(
            "Role deleted successfully.",
          );

          this.loadRoles();
        },


        error: (error) => {

          console.error(
            "Role Delete Error:",
            error,
          );


          this.showError(
            error,
            "Unable to delete role.",
          );
        },

      });
  }


  // ============================================================
  // PREVIOUS PAGE
  // ============================================================

  previousPage(): void {

    if (
      this.pageNo > 1
    ) {

      this.pageNo--;
    }
  }


  // ============================================================
  // NEXT PAGE
  // ============================================================

  nextPage(): void {

    if (
      this.pageNo <
      this.totalPages
    ) {

      this.pageNo++;
    }
  }


  // ============================================================
  // RESET FORM
  // ============================================================

  private resetForm(): void {

    this.form = {

      roleId: 0,

      roleName: "",

      roleCode: "",

      roleDesc: "",

      groupName: "",

      moduleName: "",

      moduleId: 0,

      isActive: 1,

    };


    this.moduleEntities = [];

    this.loadingModuleEntities =
      false;
  }


  // ============================================================
  // ENSURE CURRENT PAGE
  // ============================================================

  private ensurePage(): void {

    if (
      this.pageNo >
      this.totalPages
    ) {

      this.pageNo =
        this.totalPages;
    }


    if (
      this.pageNo < 1
    ) {

      this.pageNo = 1;
    }
  }


  // ============================================================
  // MODULE NORMALIZER
  // ============================================================

  private normalizeModuleName(
    value: string,
  ): string {

    const moduleName =
      String(
        value || "",
      )
        .trim()
        .toLowerCase();


    if (
      moduleName ===
      "housing society"
    ) {

      return "society";
    }


    if (
      moduleName === "global"
    ) {

      return "common";
    }


    return moduleName;
  }


  // ============================================================
  // SLUGIFY
  // ============================================================

  private slugify(
    value: string,
  ): string {

    return String(
      value || "",
    )
      .trim()
      .toLowerCase()
      .replace(
        /[^a-z0-9]+/g,
        "-",
      )
      .replace(
        /^-+|-+$/g,
        "",
      );
  }


  // ============================================================
  // EXTRACT ARRAY
  // ============================================================

  private extractArray(
    response: any,
  ): any[] {

    if (!response) {

      return [];
    }


    if (
      Array.isArray(
        response,
      )
    ) {

      return response;
    }


    const candidates = [

      response?.items,
      response?.Items,

      response?.data,
      response?.Data,

      response?.data?.items,
      response?.data?.Items,

      response?.Data?.items,
      response?.Data?.Items,

      response?.result,
      response?.Result,

      response?.result?.items,
      response?.result?.Items,

      response?.Result?.items,
      response?.Result?.Items,

      response?.responseData,
      response?.ResponseData,

      response?.responseData?.items,
      response?.responseData?.Items,

      response?.ResponseData?.items,
      response?.ResponseData?.Items,

      response?.list,
      response?.List,

      response?.value,
      response?.Value,

    ];


    for (
      const candidate
      of candidates
    ) {

      if (
        Array.isArray(
          candidate,
        )
      ) {

        return candidate;
      }
    }


    const deepCandidates = [

      response?.items?.data,

      response?.items?.items,

      response?.data?.data,

      response?.result?.data,

      response?.responseData?.data,

    ];


    for (
      const candidate
      of deepCandidates
    ) {

      if (
        Array.isArray(
          candidate,
        )
      ) {

        return candidate;
      }
    }


    return [];
  }


  // ============================================================
  // NORMALIZE ROLE
  // ============================================================

  private normalizeRole(
    item: any,
  ): RoleItem {

    return {

      roleId:
        Number(
          item?.roleId ??
          item?.RoleId ??
          0,
        ),

      roleName:
        String(
          item?.roleName ??
          item?.RoleName ??
          "",
        ),

      roleCode:
        String(
          item?.roleCode ??
          item?.RoleCode ??
          "",
        ),

      roleDesc:
        String(
          item?.roleDesc ??
          item?.RoleDesc ??
          "",
        ),

      groupName:
        String(
          item?.groupName ??
          item?.GroupName ??
          "",
        ),

      moduleName:
        String(
          item?.moduleName ??
          item?.ModuleName ??
          "",
        ),

      moduleId:
        Number(
          item?.moduleId ??
          item?.ModuleId ??
          0,
        ),

      isActive:
        Number(
          item?.isActive ??
          item?.IsActive ??
          1,
        ),

      isDeleted:
        Number(
          item?.isDeleted ??
          item?.IsDeleted ??
          0,
        ),

      createdBy:
        item?.createdBy ??
        item?.CreatedBy,

      createdOn:
        item?.createdOn ??
        item?.CreatedOn,

    };
  }


  // ============================================================
  // ACTION USER
  // ============================================================

  private getActionUser(): string {

    const actionUser =
      (
        localStorage.getItem(
          "userId",
        )

        ||

        localStorage.getItem(
          "UserId",
        )

        ||

        "SYSTEM"
      )
        .replace(
          /^"|"$/g,
          "",
        )
        .trim();


    return (
      actionUser ||
      "SYSTEM"
    );
  }


  // ============================================================
  // API ERROR
  // ============================================================

  private showError(
    error: any,
    fallback: string,
  ): void {

    let message =

      error?.error?.message

      ??

      error?.error?.Message

      ??

      error?.error?.title

      ??

      error?.error?.Title

      ??

      error?.message

      ??

      fallback;


    if (
      typeof error?.error ===
        "string"

      &&

      error.error.trim()
    ) {

      message =
        error.error.trim();
    }


    this.toastr.error(
      String(
        message ||
        fallback,
      ),
    );
  }
}